import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DocumentAttachmentPanel = ({ documents, onUpload, onDelete, onDownload }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      onUpload(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      onUpload(e?.target?.files);
    }
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('image')) return 'Image';
    if (type?.includes('word') || type?.includes('document')) return 'FileText';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'Table';
    return 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Paperclip" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Dokumente</h3>
        </div>
        <span className="text-sm text-muted-foreground">
          {documents?.length} {documents?.length === 1 ? 'Dokument' : 'Dokumente'}
        </span>
      </div>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={32} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              Dateien hier ablegen oder
            </p>
            <label className="cursor-pointer">
              <span className="text-sm text-primary hover:underline">
                Dateien auswählen
              </span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileInput}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            PDF, DOC, DOCX, JPG, PNG, XLS, XLSX (max. 10 MB pro Datei)
          </p>
        </div>
      </div>
      {/* Document List */}
      {documents?.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-foreground">Hochgeladene Dokumente</h4>
          <div className="space-y-2">
            {documents?.map((doc) => (
              <div
                key={doc?.id}
                className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border hover:border-primary/50 transition-colors"
              >
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={getFileIcon(doc?.type)} size={20} className="text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {doc?.name}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span>{formatFileSize(doc?.size)}</span>
                    <span>•</span>
                    <span>{doc?.uploadDate}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      {doc?.uploadedBy}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  {doc?.scanStatus === 'clean' && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-success/10 rounded text-success">
                      <Icon name="ShieldCheck" size={14} />
                      <span className="text-xs">Geprüft</span>
                    </div>
                  )}
                  {doc?.scanStatus === 'scanning' && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-warning/10 rounded text-warning">
                      <Icon name="Loader" size={14} className="animate-spin" />
                      <span className="text-xs">Wird geprüft</span>
                    </div>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDownload(doc?.id)}
                    title="Herunterladen"
                  >
                    <Icon name="Download" size={18} />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(doc?.id)}
                    title="Löschen"
                  >
                    <Icon name="Trash2" size={18} className="text-error" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="mt-4 flex items-start gap-2 p-3 bg-accent/5 rounded-lg border border-accent/20">
        <Icon name="Shield" size={16} className="text-accent mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Alle hochgeladenen Dokumente werden automatisch auf Viren gescannt und verschlüsselt gespeichert. 
          Die Aufbewahrung erfolgt gemäß DSGVO-Richtlinien.
        </p>
      </div>
    </div>
  );
};

export default DocumentAttachmentPanel;